import React from 'react';
import { Typography as AntTypography } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

// Export all sub-components types
export type TitleLevel = 1 | 2 | 3 | 4 | 5;

export interface TitleProps {
  level?: TitleLevel;
  className?: string;
  code?: boolean;
  copyable?: boolean | object;
  delete?: boolean;
  disabled?: boolean;
  editable?: boolean | object;
  ellipsis?: boolean | object;
  mark?: boolean;
  underline?: boolean;
  type?: 'secondary' | 'success' | 'warning' | 'danger';
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

export interface TextProps {
  className?: string;
  code?: boolean;
  copyable?: boolean | object;
  delete?: boolean;
  disabled?: boolean;
  editable?: boolean | object;
  ellipsis?: boolean | object;
  keyboard?: boolean;
  mark?: boolean;
  strong?: boolean;
  underline?: boolean;
  type?: 'secondary' | 'success' | 'warning' | 'danger';
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

export interface ParagraphProps {
  className?: string;
  code?: boolean;
  copyable?: boolean | object;
  delete?: boolean;
  disabled?: boolean;
  editable?: boolean | object;
  ellipsis?: boolean | object;
  mark?: boolean;
  underline?: boolean;
  type?: 'secondary' | 'success' | 'warning' | 'danger';
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

export interface LinkProps {
  className?: string;
  href?: string;
  target?: string;
  ellipsis?: boolean;
  underline?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

const useStyles = createUseStyles((theme: ThemeType) => ({
  // Title variants
  titleH1: {
    '&.ant-typography': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.xxl,
      fontWeight: theme.typography.fontWeight.bold,
      lineHeight: theme.typography.lineHeight.loose,
      marginBottom: theme.spacing[3],
    },
  },
  titleH2: {
    '&.ant-typography': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.xl,
      fontWeight: theme.typography.fontWeight.bold,
      lineHeight: theme.typography.lineHeight.relaxed,
      marginBottom: theme.spacing[3],
    },
  },
  titleH3: {
    '&.ant-typography': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.lg,
      fontWeight: theme.typography.fontWeight.semibold,
      lineHeight: theme.typography.lineHeight.normal,
      marginBottom: theme.spacing[2],
    },
  },
  titleH4: {
    '&.ant-typography': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.md,
      fontWeight: theme.typography.fontWeight.semibold,
      lineHeight: theme.typography.lineHeight.normal,
      marginBottom: theme.spacing[2],
    },
  },
  titleH5: {
    '&.ant-typography': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.semibold,
      lineHeight: theme.typography.lineHeight.normal,
      marginBottom: theme.spacing[2],
    },
  },

  // Text variants
  textBody: {
    '&.ant-typography': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
    },
  },
  textSecondary: {
    '&.ant-typography': {
      color: theme.color.semantic.text.secondary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
    },
  },
  textDisabled: {
    '&.ant-typography': {
      color: theme.color.semantic.text.soft,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
    },
  },
  textSuccess: {
    '&.ant-typography': {
      color: theme.color.semantic.accent.green.strong,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
    },
  },
  textWarning: {
    '&.ant-typography': {
      color: theme.color.semantic.accent.orange.strong,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
    },
  },
  textDanger: {
    '&.ant-typography': {
      color: theme.color.semantic.brand.danger.primary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
    },
  },

  // Link
  link: {
    '&.ant-typography': {
      color: theme.color.semantic.brand.default.primary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
      '&:hover': {
        color: theme.color.semantic.brand.default.secondary,
      },
      '&:active': {
        color: theme.color.semantic.brand.default.strong,
      },
    },
  },

  // Paragraph
  paragraph: {
    '&.ant-typography': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
      marginBottom: theme.spacing[3],
    },
  },

  // Special formats
  mark: {
    '&.ant-typography mark': {
      backgroundColor: theme.color.semantic.accent.orange.subtle,
      color: theme.color.semantic.text.primary,
    },
  },
  code: {
    '&.ant-typography code': {
      backgroundColor: theme.color.semantic.background.neutral.subtle,
      color: theme.color.semantic.brand.danger.primary,
      padding: `${theme.spacing[0]} ${theme.spacing[1]}`,
      fontSize: theme.typography.fontSize.xs,
      borderRadius: theme.radius.sm,
    },
  },
  keyboard: {
    '&.ant-typography kbd': {
      backgroundColor: theme.color.semantic.background.neutral.subtle,
      color: theme.color.semantic.text.primary,
      border: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
      padding: `${theme.spacing[0]} ${theme.spacing[1]}`,
      fontSize: theme.typography.fontSize.xs,
      borderRadius: theme.radius.sm,
    },
  },
  delete: {
    '&.ant-typography': {
      color: theme.color.semantic.text.soft,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
    },
  },
  underline: {
    '&.ant-typography': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
    },
  },
  strong: {
    '&.ant-typography': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.bold,
      lineHeight: theme.typography.lineHeight.normal,
    },
  },
  ellipsis: {
    '&.ant-typography': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
    },
  },
  copyable: {
    '&.ant-typography .ant-typography-copy': {
      color: theme.color.semantic.brand.default.primary,
    },
  },
  editable: {
    '&.ant-typography': {
      color: theme.color.semantic.text.primary,
    },
  },
}));

// Title component
const TitleInner: React.FC<TitleProps> = ({ level = 1, className = '', ...rest }) => {
  const classes = useStyles();

  const getTitleClass = () => {
    switch (level) {
      case 1:
        return classes.titleH1;
      case 2:
        return classes.titleH2;
      case 3:
        return classes.titleH3;
      case 4:
        return classes.titleH4;
      case 5:
        return classes.titleH5;
      default:
        return classes.titleH1;
    }
  };

  return (
    <AntTypography.Title level={level} {...rest} className={`${getTitleClass()} ${className}`.trim()} />
  );
};

// Text component
const TextInner: React.FC<TextProps> = ({ type, disabled, className = '', ...rest }) => {
  const classes = useStyles();

  const getTextClass = () => {
    if (disabled) return classes.textDisabled;
    switch (type) {
      case 'secondary':
        return classes.textSecondary;
      case 'success':
        return classes.textSuccess;
      case 'warning':
        return classes.textWarning;
      case 'danger':
        return classes.textDanger;
      default:
        return classes.textBody;
    }
  };

  return (
    <AntTypography.Text type={type} disabled={disabled} {...rest} className={`${getTextClass()} ${className}`.trim()} />
  );
};

// Paragraph component
const ParagraphInner: React.FC<ParagraphProps> = ({ className = '', ...rest }) => {
  const classes = useStyles();

  return (
    <AntTypography.Paragraph {...rest} className={`${classes.paragraph} ${className}`.trim()} />
  );
};

// Link component
const LinkInner: React.FC<LinkProps> = ({ className = '', ...rest }) => {
  const classes = useStyles();

  return (
    <AntTypography.Link {...rest} className={`${classes.link} ${className}`.trim()} />
  );
};

// Create Typography with all sub-components
export const Typography = Object.assign(
  {},
  {
    Title: TitleInner,
    Text: TextInner,
    Paragraph: ParagraphInner,
    Link: LinkInner,
  }
);

export default Typography;
